import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Button, Card, Tag, ConfigProvider } from "antd";
import { Users, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { VojomoSymbol } from "./components/VojomoSymbol";
import "./rb-globals.css";

const SERVER_URL = import.meta.env.VITE_REACT_APP_SERVER || "";

interface InviteInfo {
  sessionId: string;
  sessionName: string;
  ownerName: string;
  accessLevel: "view" | "comment" | "solo_contributor" | "team_member" | "owner";
  inviteeEmail: string;
}

const ACCESS_LEVEL_LABELS: Record<string, string> = {
  view: "review",
  comment: "review and comment on",
  solo_contributor: "contribute to",
  team_member: "collaborate on",
  owner: "co-own",
};

const ACCESS_LEVEL_DESCRIPTIONS: Record<string, string> = {
  view: "You'll be able to view the roadmap in read-only mode.",
  comment: "You'll be able to view the roadmap and leave comments and feedback.",
  solo_contributor:
    "You'll get your own copy of the roadmap to work on independently. The owner can then review and synthesize all contributions.",
  team_member:
    "You'll have full access to the team's roadmap and can contribute directly alongside the owner and other team members.",
  owner:
    "You'll have full owner access to this roadmap — edit everything, invite others, run synthesis, and lock periods, alongside the other owners.",
};

export function RoadmapBuilderInviteAccept() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, loginWithRedirect } = useAuth0();

  const [info, setInfo] = useState<InviteInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const r = await fetch(`${SERVER_URL}/api/rb/collaborators/invite-info/${token}`);
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error(err.detail || "This invite link is invalid or has already been used.");
        }
        setInfo(await r.json());
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : "Failed to load invite.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleAccept = async () => {
    if (!token) return;
    setAccepting(true);
    try {
      const r = await fetch(`${SERVER_URL}/api/rb/collaborators/accept/${token}`, {
        method: "POST",
        credentials: "include",
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to accept invite.");
      }
      const data = await r.json();
      setAccepted(true);
      // Redirect to the appropriate session after a brief moment
      setTimeout(() => {
        const target = data.soloSessionId || data.sessionId;
        if (target) {
          navigate(`/roadmap/build_roadmap/${target}`);
        } else {
          navigate("/roadmap/build_roadmap");
        }
      }, 1500);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to accept invite.");
    } finally {
      setAccepting(false);
    }
  };

  return (
    <ConfigProvider theme={{ token: { colorPrimary: "#2c1561" } }}>
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          background: "#f8fafc",
        }}
      >
        <div style={{ marginBottom: 24 }}>
          <VojomoSymbol style={{ width: 36, height: 36 }} />
        </div>

        <Card styles={{ body: { padding: 32 } }} style={{ width: "100%", maxWidth: 460 }}>
          {loading ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "16px 0" }}>
              <Loader2 style={{ width: 18, height: 18, animation: "spin 1s linear infinite" }} />
              <span className="text-sm text-muted-foreground">Loading invitation…</span>
            </div>
          ) : error ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, textAlign: "center" }}>
              <AlertCircle style={{ width: 36, height: 36, color: "#ef4444" }} />
              <p className="text-sm font-medium">Invite unavailable</p>
              <p className="text-sm text-muted-foreground">{error}</p>
              <Button onClick={() => navigate("/roadmap/build_roadmap")}>
                Go to My Roadmaps
              </Button>
            </div>
          ) : accepted ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, textAlign: "center" }}>
              <CheckCircle2 style={{ width: 36, height: 36, color: "#10b981" }} />
              <p className="text-sm font-semibold">Invitation accepted!</p>
              <p className="text-sm text-muted-foreground">Redirecting you to the roadmap…</p>
            </div>
          ) : info ? (
            <div className="space-y-4">
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    background: "#ede9fe",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Users style={{ width: 20, height: 20, color: "#2c1561" }} />
                </div>
                <div>
                  <p className="text-sm font-semibold">You're invited to collaborate</p>
                  <p className="text-xs text-muted-foreground">from {info.ownerName}</p>
                </div>
              </div>

              <div style={{ background: "#f8fafc", borderRadius: 8, padding: "12px 14px" }}>
                <p className="text-xs text-muted-foreground mb-1">Roadmap</p>
                <p className="text-sm font-medium">{info.sessionName}</p>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <p className="text-sm text-muted-foreground">
                  {info.ownerName} invited you to{" "}
                  <strong>{ACCESS_LEVEL_LABELS[info.accessLevel] || info.accessLevel}</strong> this roadmap.
                </p>
                <Tag
                  color={
                    info.accessLevel === "view"
                      ? "default"
                      : info.accessLevel === "comment"
                      ? "processing"
                      : "purple"
                  }
                  className="text-xs"
                >
                  {info.accessLevel === "view"
                    ? "View"
                    : info.accessLevel === "comment"
                    ? "Comment"
                    : info.accessLevel === "owner"
                    ? "Owner"
                    : info.accessLevel === "team_member"
                    ? "Team Member"
                    : "Solo Contributor"}
                </Tag>
              </div>

              <p className="text-xs text-muted-foreground">
                {ACCESS_LEVEL_DESCRIPTIONS[info.accessLevel]}
              </p>

              <p className="text-xs text-muted-foreground">
                This invite was sent to <strong>{info.inviteeEmail}</strong>.{" "}
                {isAuthenticated
                  ? "Make sure you're logged in with that account."
                  : "Log in or create a free account to accept."}
              </p>

              {isAuthenticated ? (
                <Button
                  type="primary"
                  block
                  loading={accepting}
                  onClick={handleAccept}
                  size="large"
                >
                  Accept Invitation
                </Button>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <Button
                    type="primary"
                    block
                    size="large"
                    onClick={() =>
                      loginWithRedirect({
                        authorizationParams: { screen_hint: "signup" },
                        appState: { returnTo: window.location.href },
                      })
                    }
                  >
                    Create account &amp; accept
                  </Button>
                  <Button
                    block
                    size="large"
                    onClick={() =>
                      loginWithRedirect({
                        appState: { returnTo: window.location.href },
                      })
                    }
                  >
                    Log in to accept
                  </Button>
                </div>
              )}
            </div>
          ) : null}
        </Card>
      </div>
    </ConfigProvider>
  );
}
